import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo, faqItems } from '@/mocks/data';

export default function AboutPage() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brandName,
    url: 'https://hzhgjs.cn',
    description: aboutInfo.introduction,
    email: aboutInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: aboutInfo.contact.email,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${aboutInfo.brandName}`,
    description: aboutInfo.introduction,
    url: 'https://hzhgjs.cn/about',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />

        <main className="flex-1 pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Page Title */}
            <section className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                关于我们
              </h1>
              <p className="text-foreground-600 leading-relaxed max-w-2xl">
                {aboutInfo.introduction}
              </p>
            </section>

            {/* Brand Story */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                我们的故事
              </h2>
              <div className="bg-background-100/70 rounded-lg p-6 md:p-8 border border-background-200/70">
                <p className="text-foreground-700 leading-relaxed">
                  {aboutInfo.story}
                </p>
              </div>
            </section>

            {/* Values */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                我们的理念
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {aboutInfo.values.map((value, index) => (
                  <div
                    key={index}
                    className="bg-background-100/70 rounded-lg p-5 border border-background-200/70 hover:border-primary-200/60 transition-colors"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <span className="font-heading text-lg font-semibold">{index + 1}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Team */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                我们的团队
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {aboutInfo.team.map((member, index) => (
                  <div
                    key={index}
                    className="bg-background-100/70 rounded-lg p-5 border border-background-200/70 text-center"
                  >
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center text-foreground-500">
                      <i className="ri-user-line text-xl"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900">
                      {member.name}
                    </h3>
                    <p className="text-xs text-primary-600 font-medium mb-1">{member.role}</p>
                    <p className="text-xs text-foreground-500">{member.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <details
                    key={index}
                    className="bg-background-100/70 rounded-lg border border-background-200/70 group"
                  >
                    <summary className="p-4 md:p-5 cursor-pointer flex items-center justify-between text-foreground-900 font-medium text-sm md:text-base">
                      <span>{item.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-14">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-100/70 rounded-lg p-6 border border-background-200/70">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    联系方式
                  </h3>
                  <ul className="space-y-3 text-sm text-foreground-700">
                    <li className="flex items-center gap-2">
                      <i className="ri-mail-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                      <span>{aboutInfo.contact.email}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-wechat-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                      <span>微信公众号：{aboutInfo.contact.wechat}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                      <span>{aboutInfo.contact.address}</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-background-100/70 rounded-lg p-6 border border-background-200/70">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    给我们留言
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') return;
    const submitAddr = 'https://readdy.ai/api/form/d9u0krlca23r0tr1ao50';
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt') {
        body.append(key, String(value));
      }
    });
    fetch(submitAddr, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then((res) => res.text())
      .then((text) => {
        const parsed = JSON.parse(text);
        if (parsed?.code === 'OK') {
          form.reset();
          alert('感谢您的留言！我们会尽快回复。');
        } else {
          alert('提交失败，请稍后重试。');
        }
      })
      .catch(() => {
        alert('提交失败，请稍后重试。');
      });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form="">
      <div>
        <input
          type="text"
          name="name"
          placeholder="您的姓名"
          required
          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          required
          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
        />
      </div>
      <div>
        <textarea
          name="message"
          placeholder="请输入您的留言（500字以内）"
          rows={3}
          maxLength={500}
          required
          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
        ></textarea>
      </div>
      <div className="spam-guard">
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>
      <button
        type="submit"
        className="w-full px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        发送留言
      </button>
    </form>
  );
}